import { useEffect, useMemo, useState } from "react";
import { AlignLeft, AtSign, Calendar, CalendarClock, CheckSquare, ChevronDown, CircleDot, Coins, Hash, Link2, ListChecks, Percent, Phone, ToggleLeft, Type } from "lucide-react";
import { Checkbox, Input, NativeSelect, Switch, Textarea, cn } from "@ncp/ui";
import { enumOptionsOf } from "../schema";
import type { EnumOption } from "../types";
import { registerWidget, type WidgetProps } from "./registry";

const T = {
  yes: { en: "Yes", ar: "نعم" },
  no: { en: "No", ar: "لا" },
  choose: { en: "Choose…", ar: "اختر…" },
};

function str(v: unknown): string {
  return v === undefined || v === null ? "" : String(v);
}

function Adorned({ unit, children, dir }: { unit?: string; children: React.ReactNode; dir?: "ltr" | "rtl" }) {
  if (!unit) return <>{children}</>;
  return (
    <div className="relative" dir={dir}>
      {children}
      <span className="pointer-events-none absolute end-3 top-1/2 -translate-y-1/2 text-xs font-medium text-sand-600">{unit}</span>
    </div>
  );
}

// ---------------------------------------------------------------- text family

export function TextWidget({ id, value, onChange, onBlur, disabled, readOnly, invalid, placeholder, schema, options, required }: WidgetProps) {
  const x = schema["x-ncp"];
  const inputType = (options.inputType as string | undefined) ?? "text";
  return (
    <Adorned unit={x?.unit}>
      <Input
        id={id}
        type={inputType}
        value={str(value)}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={readOnly}
        required={required}
        aria-invalid={invalid || undefined}
        maxLength={schema.maxLength}
        dir={x?.dir ?? (inputType === "email" || inputType === "url" || inputType === "tel" ? "ltr" : undefined)}
        className={cn(x?.unit && "pe-14")}
        autoComplete={(options.autoComplete as string | undefined) ?? "off"}
      />
    </Adorned>
  );
}

export function TextareaWidget({ id, path, value, onChange, onBlur, disabled, readOnly, invalid, placeholder, schema, options, context }: WidgetProps) {
  const rows = Number(options.rows ?? 4);
  const max = schema.maxLength;
  const len = str(value).length;
  // What the person writes is kept on the device per record and field until the server has it, when the host names the record (S 2.7).
  const draft = context.draftKey && !readOnly && !disabled ? `${context.draftKey}:${path}` : null;
  return (
    <div className="space-y-1">
      <Textarea id={id} value={str(value)} onChange={(e) => onChange(e.target.value)} onBlur={onBlur} placeholder={placeholder} disabled={disabled} readOnly={readOnly} aria-invalid={invalid || undefined} rows={rows} maxLength={max} dir={schema["x-ncp"]?.dir} draft={draft} />
      {max ? <p className={cn("text-end text-[11px] tabular-nums text-sand-600", len > max * 0.9 && "text-warning-700")}>{len}/{max}</p> : null}
    </div>
  );
}

// ---------------------------------------------------------------- numbers

function parseNumber(raw: string, locale: string): number | undefined {
  const digitsAr = "٠١٢٣٤٥٦٧٨٩";
  let s = raw.trim();
  if (!s) return undefined;
  s = s.replace(/[٠-٩]/g, (d) => String(digitsAr.indexOf(d)));
  s = s.replace(/[\s,]/g, locale === "ar" ? "" : "").replace(/٫/g, ".");
  const n = Number(s);
  return Number.isFinite(n) ? n : undefined;
}

export function NumberWidget({ id, value, onChange, onBlur, disabled, readOnly, invalid, placeholder, schema, options, locale }: WidgetProps) {
  const x = schema["x-ncp"];
  const isInt = schema.type === "integer";
  const format = (options.format as string | undefined) ?? (schema.format === "currency" ? "currency" : schema.format === "percent" ? "percent" : "plain");
  const currency = (options.currency as string | undefined) ?? "AED";
  const grouping = format === "currency" || options.grouping === true;
  const fmt = useMemo(() => new Intl.NumberFormat(locale === "ar" ? "ar-AE" : "en-AE", { maximumFractionDigits: isInt ? 0 : Number(options.decimals ?? 2), useGrouping: grouping }), [locale, isInt, options.decimals, grouping]);
  const [text, setText] = useState(() => (typeof value === "number" ? (grouping ? fmt.format(value) : String(value)) : ""));
  const [focused, setFocused] = useState(false);
  useEffect(() => {
    if (!focused) setText(typeof value === "number" ? (grouping ? fmt.format(value) : String(value)) : "");
  }, [value, focused, fmt, grouping]);
  const unit = format === "currency" ? currency : format === "percent" ? "%" : x?.unit;
  return (
    <Adorned unit={unit} dir="ltr">
      <Input
        id={id}
        inputMode={isInt ? "numeric" : "decimal"}
        value={text}
        onFocus={() => {
          setFocused(true);
          if (typeof value === "number") setText(String(value));
        }}
        onChange={(e) => {
          setText(e.target.value);
          const n = parseNumber(e.target.value, locale);
          onChange(n === undefined ? undefined : isInt ? Math.trunc(n) : n);
        }}
        onBlur={() => {
          setFocused(false);
          onBlur();
        }}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={readOnly}
        aria-invalid={invalid || undefined}
        dir="ltr"
        className={cn("tabular-nums", unit && "pe-14")}
        min={schema.minimum}
        max={schema.maximum}
      />
    </Adorned>
  );
}

// ---------------------------------------------------------------- dates

export function DateWidget({ id, value, onChange, onBlur, disabled, readOnly, invalid, schema, options }: WidgetProps) {
  const withTime = schema.format === "date-time" || options.time === true;
  const v = str(value);
  return (
    <Input
      id={id}
      type={withTime ? "datetime-local" : "date"}
      value={withTime ? v.slice(0, 16) : v.slice(0, 10)}
      onChange={(e) => onChange(e.target.value ? (withTime ? new Date(e.target.value).toISOString() : e.target.value) : undefined)}
      onBlur={onBlur}
      disabled={disabled}
      readOnly={readOnly}
      aria-invalid={invalid || undefined}
      min={options.min as string | undefined}
      max={options.max as string | undefined}
      dir="ltr"
    />
  );
}

// ---------------------------------------------------------------- choices

function useOptions(props: WidgetProps): Array<{ value: string; label: string; description?: string; deprecated?: boolean }> {
  const { schema, locale, options, context } = props;
  const source = options.source as string | undefined;
  const [remote, setRemote] = useState<EnumOption[] | null>(null);
  useEffect(() => {
    if (!source || !context.lookup) return;
    let alive = true;
    context.lookup(source).then((r) => alive && setRemote(r)).catch(() => alive && setRemote([]));
    return () => {
      alive = false;
    };
  }, [source, context]);
  return useMemo(() => {
    if (remote) return remote.map((o) => ({ value: String(o.value), label: typeof o.label === "string" ? o.label : o.label[locale] ?? o.label.en ?? String(o.value), description: undefined, deprecated: o.deprecated }));
    return enumOptionsOf(schema, locale);
  }, [remote, schema, locale]);
}

export function SelectWidget(props: WidgetProps) {
  const { id, value, onChange, onBlur, disabled, readOnly, invalid, locale, required } = props;
  const opts = useOptions(props);
  const current = str(value);
  return (
    <NativeSelect id={id} value={current} onChange={(e) => onChange(coerceEnum(e.target.value, props))} onBlur={onBlur} disabled={disabled || readOnly} aria-invalid={invalid || undefined} required={required}>
      <option value="">{T.choose[locale]}</option>
      {opts.filter((o) => !o.deprecated || o.value === current).map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </NativeSelect>
  );
}

function coerceEnum(raw: string, props: WidgetProps): unknown {
  if (raw === "") return undefined;
  const values = props.schema.enum ?? props.schema.items?.enum ?? [];
  const match = values.find((v) => String(v) === raw);
  return match === undefined ? raw : match;
}

export function RadioWidget(props: WidgetProps) {
  const { id, value, onChange, onBlur, disabled, readOnly, invalid, options } = props;
  const opts = useOptions(props);
  const current = str(value);
  const inline = options.inline === true || opts.length <= 2;
  return (
    <div role="radiogroup" aria-labelledby={`${id}-label`} aria-invalid={invalid || undefined} className={cn("flex gap-2", inline ? "flex-wrap" : "flex-col")}>
      {opts.filter((o) => !o.deprecated || o.value === current).map((o) => {
        const checked = current === o.value;
        return (
          <label
            key={o.value}
            className={cn(
              "flex cursor-pointer items-start gap-2.5 rounded-lg border px-3 py-2 text-sm transition-colors",
              checked ? "border-aegold-400 bg-aegold-50" : "border-sand-300 bg-white/70 hover:border-aegold-300",
              (disabled || readOnly) && "cursor-not-allowed opacity-60",
              invalid && !checked && "border-danger-300",
            )}
          >
            <input type="radio" name={id} className="sr-only" value={o.value} checked={checked} disabled={disabled || readOnly} onChange={() => onChange(coerceEnum(o.value, props))} onBlur={onBlur} />
            <span className={cn("mt-0.5 grid size-4 shrink-0 place-items-center rounded-full border", checked ? "border-aegold-600" : "border-sand-400")} aria-hidden>
              {checked ? <span className="size-2 rounded-full bg-aegold-600" /> : null}
            </span>
            <span className="min-w-0">
              <span className="block font-medium leading-tight">{o.label}</span>
              {o.description ? <span className="block text-xs text-muted-foreground">{o.description}</span> : null}
            </span>
          </label>
        );
      })}
    </div>
  );
}

export function MultiSelectWidget(props: WidgetProps) {
  const { id, value, onChange, onBlur, disabled, readOnly, invalid, schema } = props;
  const opts = useOptions(props);
  const selected = Array.isArray(value) ? value.map(String) : [];
  const max = schema.maxItems;
  const toggle = (v: string) => {
    const next = selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v];
    const raw = next.map((s) => coerceEnum(s, { ...props, schema: schema.items ?? schema }));
    onChange(raw);
  };
  return (
    <div role="group" aria-labelledby={`${id}-label`} aria-invalid={invalid || undefined} className="grid gap-2 sm:grid-cols-2">
      {opts.map((o) => {
        const checked = selected.includes(o.value);
        const atMax = !!max && !checked && selected.length >= max;
        return (
          <label key={o.value} className={cn("flex cursor-pointer items-start gap-2.5 rounded-lg border px-3 py-2 text-sm", checked ? "border-aegold-400 bg-aegold-50" : "border-sand-300 bg-white/70", (disabled || readOnly || atMax) && "cursor-not-allowed opacity-60")}>
            <Checkbox className="mt-0.5" checked={checked} disabled={disabled || readOnly || atMax} onCheckedChange={() => toggle(o.value)} onBlur={onBlur} />
            <span className="min-w-0">
              <span className="block font-medium leading-tight">{o.label}</span>
              {o.description ? <span className="block text-xs text-muted-foreground">{o.description}</span> : null}
            </span>
          </label>
        );
      })}
    </div>
  );
}

export function CheckboxWidget({ id, value, onChange, onBlur, disabled, readOnly, invalid, label, schema, locale }: WidgetProps) {
  const hint = schema["x-ncp"]?.hint;
  return (
    <label htmlFor={id} className={cn("flex cursor-pointer items-start gap-2.5 rounded-lg border px-3 py-2.5 text-sm", value === true ? "border-aegold-400 bg-aegold-50" : "border-sand-300 bg-white/70", invalid && "border-danger-400", (disabled || readOnly) && "cursor-not-allowed opacity-60")}>
      <Checkbox id={id} className="mt-0.5" checked={value === true} disabled={disabled || readOnly} onCheckedChange={(v) => onChange(v === true)} onBlur={onBlur} aria-invalid={invalid || undefined} />
      <span className="min-w-0">
        <span className="block font-medium leading-tight">{label}</span>
        {hint ? <span className="block text-xs text-muted-foreground">{typeof hint === "string" ? hint : hint[locale] ?? hint.en}</span> : null}
      </span>
    </label>
  );
}

export function SwitchWidget({ id, value, onChange, onBlur, disabled, readOnly, label }: WidgetProps) {
  return (
    <label htmlFor={id} className="flex items-center justify-between gap-3 rounded-lg border border-sand-300 bg-white/70 px-3 py-2 text-sm">
      <span className="font-medium">{label}</span>
      <Switch id={id} checked={value === true} disabled={disabled || readOnly} onCheckedChange={(v) => onChange(v)} onBlur={onBlur} />
    </label>
  );
}

export function YesNoWidget({ id, value, onChange, onBlur, disabled, readOnly, invalid, locale }: WidgetProps) {
  return (
    <div role="radiogroup" aria-invalid={invalid || undefined} className="inline-flex overflow-hidden rounded-lg border border-sand-300 bg-white/70">
      {[true, false].map((v) => (
        <label key={String(v)} className={cn("cursor-pointer px-4 py-2 text-sm font-medium transition-colors", value === v ? "bg-aeblack-950 text-white" : "hover:bg-sand-100", (disabled || readOnly) && "cursor-not-allowed opacity-60")}>
          <input type="radio" name={id} className="sr-only" checked={value === v} disabled={disabled || readOnly} onChange={() => onChange(v)} onBlur={onBlur} />
          {v ? T.yes[locale] : T.no[locale]}
        </label>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------- registration

export function registerBasicWidgets(): void {
  registerWidget({ key: "text", label: { en: "Text", ar: "نص" }, icon: Type, category: "basic", forTypes: ["string"], component: TextWidget, defaultSchema: () => ({ type: "string", maxLength: 200 }), optionFields: [{ key: "autoComplete", label: { en: "Autocomplete", ar: "الإكمال التلقائي" }, type: "text" }] });
  registerWidget({ key: "textarea", label: { en: "Paragraph", ar: "فقرة" }, icon: AlignLeft, category: "basic", forTypes: ["string"], component: TextareaWidget, defaultSchema: () => ({ type: "string", maxLength: 2000, "x-ncp": { widget: "textarea" } }), defaultSpan: 12, optionFields: [{ key: "rows", label: { en: "Rows", ar: "الأسطر" }, type: "number" }] });
  registerWidget({ key: "email", label: { en: "E-mail", ar: "بريد إلكتروني" }, icon: AtSign, category: "basic", forTypes: ["string"], component: (p) => <TextWidget {...p} options={{ ...p.options, inputType: "email", autoComplete: "email" }} />, defaultSchema: () => ({ type: "string", format: "email", maxLength: 254 }) });
  registerWidget({ key: "phone", label: { en: "Phone", ar: "هاتف" }, icon: Phone, category: "basic", forTypes: ["string"], component: (p) => <TextWidget {...p} options={{ ...p.options, inputType: "tel", autoComplete: "tel" }} />, defaultSchema: () => ({ type: "string", format: "phone", "x-ncp": { dir: "ltr", placeholder: { en: "+971 …", ar: "+971 …" } } }) });
  registerWidget({ key: "url", label: { en: "Web address", ar: "عنوان إلكتروني" }, icon: Link2, category: "basic", forTypes: ["string"], component: (p) => <TextWidget {...p} options={{ ...p.options, inputType: "url" }} />, defaultSchema: () => ({ type: "string", format: "uri" }) });
  registerWidget({ key: "number", label: { en: "Number", ar: "رقم" }, icon: Hash, category: "number", forTypes: ["number", "integer"], component: NumberWidget, defaultSchema: () => ({ type: "number" }), optionFields: [{ key: "decimals", label: { en: "Decimals", ar: "المنازل العشرية" }, type: "number" }, { key: "grouping", label: { en: "Thousands separator", ar: "فاصل الآلاف" }, type: "boolean" }] });
  registerWidget({ key: "integer", label: { en: "Whole number", ar: "عدد صحيح" }, icon: Hash, category: "number", forTypes: ["integer"], component: NumberWidget, defaultSchema: () => ({ type: "integer", minimum: 0 }) });
  registerWidget({ key: "currency", label: { en: "Amount (AED)", ar: "مبلغ (درهم)" }, icon: Coins, category: "number", forTypes: ["number"], component: (p) => <NumberWidget {...p} options={{ ...p.options, format: "currency" }} />, defaultSchema: () => ({ type: "number", format: "currency", minimum: 0, "x-ncp": { widget: "currency", options: { currency: "AED" } } }), optionFields: [{ key: "currency", label: { en: "Currency", ar: "العملة" }, type: "select", choices: ["AED", "USD", "EUR", "GBP", "SAR"] }] });
  registerWidget({ key: "percent", label: { en: "Percentage", ar: "نسبة مئوية" }, icon: Percent, category: "number", forTypes: ["number"], component: (p) => <NumberWidget {...p} options={{ ...p.options, format: "percent" }} />, defaultSchema: () => ({ type: "number", format: "percent", minimum: 0, maximum: 100, "x-ncp": { widget: "percent" } }) });
  registerWidget({ key: "date", label: { en: "Date", ar: "تاريخ" }, icon: Calendar, category: "date", forTypes: ["string"], component: DateWidget, defaultSchema: () => ({ type: "string", format: "date" }), optionFields: [{ key: "min", label: { en: "Earliest (YYYY-MM-DD)", ar: "الأقدم" }, type: "text" }, { key: "max", label: { en: "Latest (YYYY-MM-DD)", ar: "الأحدث" }, type: "text" }] });
  registerWidget({ key: "datetime", label: { en: "Date & time", ar: "تاريخ ووقت" }, icon: CalendarClock, category: "date", forTypes: ["string"], component: (p) => <DateWidget {...p} options={{ ...p.options, time: true }} />, defaultSchema: () => ({ type: "string", format: "date-time" }) });
  registerWidget({ key: "select", label: { en: "Dropdown", ar: "قائمة منسدلة" }, icon: ChevronDown, category: "choice", forTypes: ["string", "number", "integer"], component: SelectWidget, defaultSchema: () => ({ type: "string", enum: ["optionA", "optionB"], "x-ncp": { widget: "select", enumOptions: [{ value: "optionA", label: { en: "Option A", ar: "الخيار أ" } }, { value: "optionB", label: { en: "Option B", ar: "الخيار ب" } }] } }), optionFields: [{ key: "source", label: { en: "Reference list", ar: "قائمة مرجعية" }, type: "select", choices: ["", "countries", "emirates", "sectors", "legalForms", "currencies"], hint: { en: "Loads options from platform reference data instead of the fixed list.", ar: "يحمل الخيارات من البيانات المرجعية للمنصة بدلا من القائمة الثابتة." } }] });
  registerWidget({ key: "radio", label: { en: "Single choice", ar: "اختيار واحد" }, icon: CircleDot, category: "choice", forTypes: ["string", "number", "integer", "boolean"], component: RadioWidget, defaultSchema: () => ({ type: "string", enum: ["yes", "no", "unknown"], "x-ncp": { widget: "radio", enumOptions: [{ value: "yes", label: { en: "Yes", ar: "نعم" } }, { value: "no", label: { en: "No", ar: "لا" } }, { value: "unknown", label: { en: "Not sure", ar: "غير متأكد" } }] } }), defaultSpan: 12, optionFields: [{ key: "inline", label: { en: "Inline", ar: "في سطر واحد" }, type: "boolean" }] });
  registerWidget({ key: "multiselect", label: { en: "Multiple choice", ar: "اختيار متعدد" }, icon: ListChecks, category: "choice", forTypes: ["array"], component: MultiSelectWidget, defaultSchema: () => ({ type: "array", uniqueItems: true, items: { type: "string", enum: ["a", "b", "c"] }, "x-ncp": { widget: "multiselect", enumOptions: [{ value: "a", label: { en: "A", ar: "أ" } }, { value: "b", label: { en: "B", ar: "ب" } }, { value: "c", label: { en: "C", ar: "ج" } }] } }), defaultSpan: 12 });
  registerWidget({ key: "checkbox", label: { en: "Checkbox", ar: "خانة اختيار" }, icon: CheckSquare, category: "choice", forTypes: ["boolean"], component: CheckboxWidget, ownLabel: true, defaultSchema: () => ({ type: "boolean" }), defaultSpan: 12 });
  registerWidget({ key: "switch", label: { en: "Switch", ar: "مفتاح تبديل" }, icon: ToggleLeft, category: "choice", forTypes: ["boolean"], component: SwitchWidget, ownLabel: true, defaultSchema: () => ({ type: "boolean", "x-ncp": { widget: "switch" } }) });
  registerWidget({ key: "yesno", label: { en: "Yes / No", ar: "نعم / لا" }, icon: ToggleLeft, category: "choice", forTypes: ["boolean"], component: YesNoWidget, defaultSchema: () => ({ type: "boolean", "x-ncp": { widget: "yesno" } }) });
}
